<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import camelcaseKeys from 'camelcase-keys';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Avatar from 'next/avatar/Avatar.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import ContactAPI from 'dashboard/api/contacts';
import TicketsAPI from 'dashboard/api/tickets';
import ContractsAPI from 'dashboard/api/contracts';
import { consultaDetalleContratoJson } from 'dashboard/api/cea';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import CategorySelector from 'dashboard/components/tickets/CategorySelector.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { isTicketAttachmentMimeAllowed } from 'dashboard/helper/ticketAttachmentMime';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
  conversationChannel: {
    type: String,
    default: null,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
  contact: {
    type: Object,
    default: null,
  },
});
const emit = defineEmits(['close', 'created']);
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const CONTRACT_ADDRESS_PREFILL_MS = 500;

const { t } = useI18n();
const store = useStore();

const { isCloudFeatureEnabled } = useAccount();
const isContractsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CONTRACTS)
);

const dialogRef = ref(null);
const isCreating = ref(false);
const createNewContactDialogRef = ref(null);

// Contact search state
const contactSearchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const showContactDropdown = ref(false);
const selectedContact = ref(null);

const ticket = ref({
  title: '',
  description: '',
  priority: 'medium',
  assignee_id: '',
  team_id: '',
  contract_number: '',
  explotacion: '',
  channel: props.conversationChannel || '',
});

// Category selector state
const ticketCategoryId = ref(null);
const ticketSubcategoryId = ref(null);

// Contract validation error state
const contractNumberError = ref('');

// Custom attributes state
const customAttributeValues = ref({});
const selectedSubcategoryData = ref(null);

// File attachment state
const pendingFiles = ref([]);
const isDragging = ref(false);
const fileInputRef = ref(null);

const addFiles = files => {
  Array.from(files).forEach(file => {
    if (!isTicketAttachmentMimeAllowed(file.type)) {
      useAlert(t('TICKETS.ATTACHMENTS.INVALID_FILE_TYPE'));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      useAlert(t('TICKETS.ATTACHMENTS.FILE_TOO_LARGE'));
      return;
    }
    pendingFiles.value.push(file);
  });
};

const removeFile = index => {
  pendingFiles.value.splice(index, 1);
};

const onDragOver = e => {
  e.preventDefault();
  isDragging.value = true;
};

const onDragLeave = e => {
  if (e.currentTarget.contains(e.relatedTarget)) return;
  isDragging.value = false;
};

const onDrop = e => {
  e.preventDefault();
  isDragging.value = false;
  addFiles(e.dataTransfer.files);
};

const onFileInput = e => {
  addFiles(e.target.files);
  e.target.value = '';
};

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const currentUser = useMapGetter('getCurrentUser');
const assignToMe = ref(true);

// Tasks-style metadata controls
const PRIORITY_FLAG = {
  urgent: 'text-n-ruby-9',
  high: 'text-n-amber-9',
  medium: 'text-n-blue-9',
  low: 'text-n-slate-9',
};
const priorityOptions = computed(() => [
  { value: 'low', label: t('TICKETS.PRIORITY.LOW') },
  { value: 'medium', label: t('TICKETS.PRIORITY.MEDIUM') },
  { value: 'high', label: t('TICKETS.PRIORITY.HIGH') },
  { value: 'urgent', label: t('TICKETS.PRIORITY.URGENT') },
]);
const priorityLabel = computed(
  () =>
    priorityOptions.value.find(o => o.value === ticket.value.priority)?.label
);
const channelOptions = computed(() => [
  { value: 'whatsapp', label: t('TICKETS.CHANNELS.WHATSAPP') },
  { value: 'in_person', label: t('TICKETS.CHANNELS.IN_PERSON') },
  { value: 'phone', label: t('TICKETS.CHANNELS.PHONE') },
  { value: 'email', label: t('TICKETS.CHANNELS.EMAIL') },
  { value: 'facebook', label: t('TICKETS.CHANNELS.FACEBOOK') },
  { value: 'instagram', label: t('TICKETS.CHANNELS.INSTAGRAM') },
  { value: 'twitter', label: t('TICKETS.CHANNELS.TWITTER') },
]);
const channelLabel = computed(
  () =>
    channelOptions.value.find(o => o.value === ticket.value.channel)?.label ||
    t('TICKETS.FORM.NO_CHANNEL')
);
const teamOptions = computed(() => [
  { value: '', label: t('TICKETS.FORM.NO_TEAM') },
  ...(teams.value || []).map(tm => ({ value: tm.id, label: tm.name })),
]);
const teamLabel = computed(
  () =>
    teamOptions.value.find(o => o.value === ticket.value.team_id)?.label ||
    t('TICKETS.FORM.NO_TEAM')
);
const assigneeOptions = computed(() => [
  { value: '', label: t('TICKETS.FORM.NO_ASSIGNEE') },
  ...(agents.value || []).map(a => ({ value: a.id, label: a.name })),
]);
const assigneeLabel = computed(
  () =>
    assigneeOptions.value.find(o => o.value === ticket.value.assignee_id)
      ?.label || t('TICKETS.FORM.NO_ASSIGNEE')
);

const ticketAttributeDefinitions = computed(
  () => store.getters['attributes/getTicketAttributes'] || []
);

const ADDRESS_FIELD_MAP = {
  street: {
    placeholder: 'TICKETS.FORM.ADDRESS_STREET_PLACEHOLDER',
    colSpan: 2,
  },
  colony: {
    placeholder: 'TICKETS.FORM.ADDRESS_COLONY_PLACEHOLDER',
    colSpan: 1,
  },
  postal_code: {
    placeholder: 'TICKETS.FORM.ADDRESS_POSTAL_CODE_PLACEHOLDER',
    colSpan: 1,
  },
  city: {
    placeholder: 'TICKETS.FORM.ADDRESS_CITY_PLACEHOLDER',
    colSpan: 1,
  },
  state: {
    placeholder: 'TICKETS.FORM.ADDRESS_STATE_PLACEHOLDER',
    colSpan: 1,
  },
};

const ADDRESS_SUBFIELD_ORDER = Object.keys(ADDRESS_FIELD_MAP);
const addressCustomAttributeKey = (wf, subField) =>
  `${String(wf?.key || 'address').trim()}_${subField}`;

/** Map API/editor value to a known ADDRESS_FIELD_MAP key (snake_case). */
const mapRawSubFieldToKey = raw => {
  if (raw == null) return null;
  if (typeof raw === 'object' && raw.key != null)
    return mapRawSubFieldToKey(raw.key);
  let s = String(raw).trim();
  if (!s) return null;
  s = s
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toLowerCase();
  return ADDRESS_FIELD_MAP[s] ? s : null;
};

/**
 * Ordered sub-fields for an address workflow field.
 * - Missing or empty `sub_fields` / `subFields` → all parts (same as form builder default).
 * - Legacy configs that only stored supply-point style parts → full set so colonia/ciudad appear.
 * - Composite `address` must not rely on ticket attribute `attributeDisplayType` in the template.
 */
const getAddressSubFields = wf => {
  const raw = wf?.sub_fields ?? wf?.subFields;
  if (!Array.isArray(raw) || raw.length === 0) {
    return [...ADDRESS_SUBFIELD_ORDER];
  }
  const mapped = raw.map(mapRawSubFieldToKey).filter(Boolean);
  const unique = [...new Set(mapped)];
  if (!unique.length) return [...ADDRESS_SUBFIELD_ORDER];

  const legacySupplyPoint = new Set(['street', 'postal_code', 'state']);
  if (
    unique.length === 3 &&
    [...legacySupplyPoint].every(k => unique.includes(k))
  ) {
    return [...ADDRESS_SUBFIELD_ORDER];
  }

  return ADDRESS_SUBFIELD_ORDER.filter(k => unique.includes(k));
};

let contractAddressPrefillTimer = null;

const hasWorkflowAddressField = computed(() =>
  (selectedSubcategoryData.value?.workflow_config?.fields || []).some(
    f => f.type === 'address'
  )
);

const getGenericoContratoDTO = detail => detail?.GenericoContratoDTO || detail;

const getPuntoSuministroFromDetail = detail =>
  getGenericoContratoDTO(detail)?.puntoSuministro;

/** Text after first comma (e.g. dirCorrespondencia "1 105,COLONIA X" → colony). */
const parseColonyFromCorrespondencia = raw => {
  if (!raw || typeof raw !== 'string') return '';
  const idx = raw.indexOf(',');
  if (idx === -1) return '';
  return raw.slice(idx + 1).trim();
};

const buildStreetFromPuntoSuministro = ps => {
  if (!ps) return '';
  return [ps.calle, ps.numero, ps.bloque, ps.planta]
    .filter(Boolean)
    .join(' ')
    .trim();
};

/**
 * Maps CEA consultaDetalleContrato JSON (UAT/Occam): puntoSuministro has
 * calle/municipio/provincia; colonia and CP are often only in correspondence strings.
 */
const applyContractDetailToTicketAddress = detail => {
  const dto = getGenericoContratoDTO(detail);
  const ps = dto?.puntoSuministro;
  if (!dto && !ps) return;

  const dp = dto?.datosPersonales || {};
  const dPago = dto?.datosPago || {};

  let street = buildStreetFromPuntoSuministro(ps);
  const colonyFromPs = (ps?.colonia || ps?.barrio || '').trim();
  let colony =
    colonyFromPs ||
    parseColonyFromCorrespondencia(dp.dirCorrespondencia) ||
    parseColonyFromCorrespondencia(dPago.direccionFacturacion);

  if (!street) {
    const corr = (
      dp.dirCorrespondencia ||
      dPago.direccionFacturacion ||
      ''
    ).trim();
    const beforeComma = corr.includes(',')
      ? corr.slice(0, corr.indexOf(',')).trim()
      : '';
    if (beforeComma) street = beforeComma;
  }

  const postal = String(
    ps?.codigoPostal ?? ps?.codPostal ?? ps?.cp ?? ps?.codigo_postal ?? ''
  ).trim();
  const city = (ps?.municipio || '').trim();
  const state = (ps?.provincia || '').trim();

  const addressField = (
    selectedSubcategoryData.value?.workflow_config?.fields || []
  ).find(f => f.type === 'address');
  if (!addressField) return;

  const nextValues = { ...customAttributeValues.value };
  if (street) {
    nextValues[addressCustomAttributeKey(addressField, 'street')] = street;
  }
  if (colony) {
    nextValues[addressCustomAttributeKey(addressField, 'colony')] = colony;
  }
  if (postal) {
    nextValues[addressCustomAttributeKey(addressField, 'postal_code')] = postal;
  }
  if (city) {
    nextValues[addressCustomAttributeKey(addressField, 'city')] = city;
  }
  if (state) {
    nextValues[addressCustomAttributeKey(addressField, 'state')] = state;
  }
  customAttributeValues.value = nextValues;
};

const runContractAddressPrefill = async () => {
  const trimmed = ticket.value.contract_number?.trim() || '';
  if (!trimmed || !hasWorkflowAddressField.value) return;
  if (!/^\d+$/.test(trimmed)) return;

  const requestKey = trimmed;
  try {
    let detail = await consultaDetalleContratoJson(trimmed, 'es');
    let ps = getPuntoSuministroFromDetail(detail);
    if (
      !ps ||
      (!ps.calle && !ps.municipio && !ps.provincia && !ps.codigoPostal)
    ) {
      const resolveRes = await ContractsAPI.resolveNumber(trimmed);
      const cnttnum = resolveRes?.data?.number;
      if (cnttnum) {
        detail = await consultaDetalleContratoJson(String(cnttnum), 'es');
        ps = getPuntoSuministroFromDetail(detail);
      }
    }

    if (ticket.value.contract_number?.trim() !== requestKey) return;
    applyContractDetailToTicketAddress(detail);

    // Extract explotacion from contract detail
    const contrato =
      detail?.GenericoContratoDTO?.contrato || detail?.contrato || {};
    const explotacion = contrato.explotacion || '';
    if (explotacion) ticket.value.explotacion = explotacion;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Contract address prefill error:', error);
  }
};

const scheduleContractAddressPrefill = () => {
  if (contractAddressPrefillTimer) clearTimeout(contractAddressPrefillTimer);
  const trimmed = ticket.value.contract_number?.trim() || '';
  if (!trimmed || !hasWorkflowAddressField.value) return;
  if (!/^\d+$/.test(trimmed)) return;
  contractAddressPrefillTimer = setTimeout(() => {
    runContractAddressPrefill();
  }, CONTRACT_ADDRESS_PREFILL_MS);
};

const workflowFieldsToRender = computed(() => {
  const wfFields = selectedSubcategoryData.value?.workflow_config?.fields || [];
  return wfFields
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(wf => {
      const def = ticketAttributeDefinitions.value.find(
        d => d.attributeKey === wf.key
      );
      return def ? { ...wf, definition: def } : { ...wf, definition: null };
    });
});

/** Split address vs other workflow fields so each can be its own collapsible (collapsed by default). */
const workflowFieldGroups = computed(() => {
  const all = workflowFieldsToRender.value;
  const address = all.filter(wf => wf.type === 'address');
  const other = all.filter(wf => wf.type !== 'address');
  const groups = [];
  if (address.length) {
    groups.push({
      key: 'address',
      titleKey: 'TICKETS.SIDEBAR.ADDRESS',
      fields: address,
    });
  }
  if (other.length) {
    groups.push({
      key: 'other',
      titleKey: 'TICKETS.CUSTOM_ATTRIBUTES.TITLE',
      fields: other,
    });
  }
  return groups;
});

const workflowGroupExpanded = ref({});

watch(
  () => workflowFieldsToRender.value.map(f => f.key).join(','),
  () => {
    workflowGroupExpanded.value = {};
  }
);

const toggleWorkflowGroup = key => {
  workflowGroupExpanded.value = {
    ...workflowGroupExpanded.value,
    [key]: !workflowGroupExpanded.value[key],
  };
};

const isWorkflowGroupOpen = key => !!workflowGroupExpanded.value[key];

const isFormValid = computed(() => {
  if (
    !ticket.value.title.trim() ||
    !ticketCategoryId.value ||
    !ticketSubcategoryId.value ||
    !ticket.value.channel
  ) {
    return false;
  }
  const hasEmptyRequired = workflowFieldsToRender.value.some(wf => {
    if (wf.priority !== 'required') return false;
    if (wf.type === 'address') {
      return getAddressSubFields(wf).some(
        sf => !customAttributeValues.value[addressCustomAttributeKey(wf, sf)]
      );
    }
    return !customAttributeValues.value[wf.key];
  });
  return !hasEmptyRequired;
});

// Show add contact button only when no contact is pre-linked (from tickets tab)
const showAddContactButton = computed(() => {
  return !props.contact && !props.contactId;
});

// Search contacts by name using GET endpoint (more reliable)
const searchContacts = async query => {
  if (!query || query.length < 2) {
    searchResults.value = [];
    showContactDropdown.value = false;
    return;
  }

  isSearching.value = true;
  showContactDropdown.value = true;

  try {
    // Use the search endpoint (GET) instead of filter (POST) to avoid auth issues
    const response = await ContactAPI.search(query, 1, 'name', '');

    const contacts = camelcaseKeys(response.data.payload || [], { deep: true });
    searchResults.value = contacts;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error searching contacts:', error);
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const debouncedSearch = debounce(searchContacts, 300);

const handleContactInput = () => {
  debouncedSearch(contactSearchQuery.value);
};

const selectContact = contact => {
  selectedContact.value = contact;
  contactSearchQuery.value = '';
  searchResults.value = [];
  showContactDropdown.value = false;
};

const clearSelectedContact = () => {
  selectedContact.value = null;
};

const openCreateNewContactDialog = async () => {
  await createNewContactDialogRef.value?.contactsFormRef?.resetValidation();
  createNewContactDialogRef.value?.dialogRef?.open();
};

const onContactCreated = async contact => {
  try {
    const response = await store.dispatch('contacts/create', contact);
    createNewContactDialogRef.value?.onSuccess();

    // Auto-select the newly created contact
    selectedContact.value = {
      id: response.id,
      name:
        contact.firstName + (contact.lastName ? ' ' + contact.lastName : ''),
      email: contact.email,
      thumbnail: response.thumbnail || null,
    };

    useAlert(t('TICKETS.FORM.CONTACT_CREATED_SUCCESS'));
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS_DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER_DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const resetForm = () => {
  ticket.value = {
    title: '',
    description: '',
    priority: 'medium',
    assignee_id: '',
    team_id: '',
    contract_number: '',
    explotacion: '',
    channel: props.conversationChannel || '',
  };
  selectedContact.value = null;
  contactSearchQuery.value = '';
  searchResults.value = [];
  ticketCategoryId.value = null;
  ticketSubcategoryId.value = null;
  customAttributeValues.value = {};
  selectedSubcategoryData.value = null;
  assignToMe.value = true;
  pendingFiles.value = [];
  contractNumberError.value = '';
};

// Handle category change to auto-set priority and capture subcategory data
const handleCategoryChange = ({ subcategoryData }) => {
  selectedSubcategoryData.value = subcategoryData || null;
  customAttributeValues.value = {};

  if (subcategoryData?.default_priority) {
    const priorityMap = {
      1: 'low',
      2: 'medium',
      3: 'high',
      4: 'urgent',
    };
    ticket.value.priority =
      priorityMap[subcategoryData.default_priority] || 'medium';
  }
};

const onClose = () => {
  emit('close');
  resetForm();
};

const createTicket = async () => {
  if (!isFormValid.value) return;

  contractNumberError.value = '';
  isCreating.value = true;
  try {
    const ticketData = { ...ticket.value };

    if (assignToMe.value && currentUser.value) {
      ticketData.assignee_id = currentUser.value.id;
      ticketData.status = 'in_progress';
    }

    // Add category and subcategory
    if (ticketCategoryId.value) {
      ticketData.ticket_category_id = ticketCategoryId.value;
    }
    if (ticketSubcategoryId.value) {
      ticketData.ticket_subcategory_id = ticketSubcategoryId.value;
    }

    // Link to selected contact
    if (selectedContact.value) {
      ticketData.contact_id = selectedContact.value.id;
      ticketData.client_name = selectedContact.value.name;
    }

    if (props.conversationId) {
      ticketData.conversation_id = props.conversationId;
    }
    if (props.contactId) {
      ticketData.contact_id = props.contactId;
    }

    // Add custom attributes from workflow fields
    if (Object.keys(customAttributeValues.value).length > 0) {
      ticketData.custom_attributes = { ...customAttributeValues.value };
    }

    // Remove empty fields
    Object.keys(ticketData).forEach(key => {
      if (ticketData[key] === '') {
        delete ticketData[key];
      }
    });

    const createdTicket = await store.dispatch(
      'tickets/createTicket',
      ticketData
    );

    if (pendingFiles.value.length > 0 && createdTicket?.display_id) {
      await Promise.all(
        pendingFiles.value.map(file =>
          TicketsAPI.uploadAttachment(createdTicket.display_id, file).catch(
            () => useAlert(t('TICKETS.ATTACHMENTS.UPLOAD_ERROR'))
          )
        )
      );
    }

    emit('created');
    dialogRef.value?.close();
    resetForm();
  } catch (error) {
    const payload =
      error.createTicketPayload ??
      (typeof error.response?.data === 'string'
        ? (() => {
            try {
              return JSON.parse(error.response.data);
            } catch {
              return null;
            }
          })()
        : error.response?.data);

    const contractInvalid =
      error.contractInvalid === true ||
      (payload &&
        typeof payload === 'object' &&
        (payload.contract_invalid === true ||
          payload.contractInvalid === true ||
          payload.contract_invalid === 'true' ||
          payload.contractInvalid === 'true'));

    let backendMessage = '';
    if (typeof payload?.error === 'string') {
      backendMessage = payload.error;
    } else if (typeof payload?.message === 'string') {
      backendMessage = payload.message;
    }

    if (contractInvalid) {
      contractNumberError.value =
        backendMessage || t('TICKETS.FORM.CONTRACT_NOT_FOUND');
      useAlert(contractNumberError.value);
    } else {
      useAlert(backendMessage || t('TICKETS.CREATE_ERROR'));
    }
  } finally {
    isCreating.value = false;
  }
};

const loadContact = async contactId => {
  if (!contactId) return;

  try {
    const response = await ContactAPI.show(contactId);

    if (response.data && response.data.payload) {
      const contact = camelcaseKeys(response.data.payload.contact, {
        deep: true,
      });
      selectedContact.value = contact;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading contact:', error);
  }
};

onMounted(async () => {
  if (props.show) {
    dialogRef.value?.open();
  }
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');

  // If contact object is provided directly (from conversation), use it
  if (props.contact) {
    selectedContact.value = props.contact;
  }
  // Otherwise, if contactId is provided, load and select the contact
  else if (props.contactId) {
    await loadContact(props.contactId);
  }
});

// Watch for contact object changes
watch(
  () => props.contact,
  newContact => {
    if (newContact && !selectedContact.value) {
      selectedContact.value = newContact;
    }
  }
);

// Watch for contactId changes in case it's set after mount
watch(
  () => props.contactId,
  newContactId => {
    if (newContactId && !selectedContact.value && !props.contact) {
      loadContact(newContactId);
    }
  }
);

watch(
  () => [ticket.value.contract_number, selectedSubcategoryData.value],
  () => {
    contractNumberError.value = '';
    scheduleContractAddressPrefill();
  }
);

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :is-loading="isCreating"
    :disable-confirm-button="!isFormValid"
    :confirm-button-label="$t('TICKETS.FORM.CREATE')"
    :cancel-button-label="$t('TICKETS.FORM.CANCEL')"
    @confirm="createTicket"
    @close="onClose"
  >
    <!-- Tasks-style create form -->
    <div class="max-h-[72vh] min-h-0 space-y-6 overflow-y-auto px-1 pb-1">
      <!-- Title (hero) -->
      <input
        v-model="ticket.title"
        type="text"
        :aria-label="$t('TICKETS.CREATE_MODAL.TITLE')"
        :placeholder="$t('TICKETS.FORM.TITLE_PLACEHOLDER')"
        class="w-full text-xl font-bold leading-snug bg-transparent rounded-md reset-base text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:bg-n-alpha-1 focus:px-2 focus:-mx-2 focus:py-1"
      />

      <!-- Contacto -->
      <div class="flex items-start gap-3 min-w-0">
        <span
          class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
        >
          <span class="i-lucide-contact text-n-slate-10" />
          {{ $t('TICKETS.FORM.CONTACT') }}
        </span>
        <div class="flex flex-col flex-1 min-w-0 gap-1.5">
          <!-- Selected contact -->
          <div
            v-if="selectedContact"
            class="flex items-center gap-2 px-2 py-1.5 border rounded-md bg-n-alpha-black2 border-n-weak"
          >
            <Avatar
              :name="selectedContact.name"
              :src="selectedContact.thumbnail"
              :size="22"
            />
            <span class="flex-1 text-sm truncate text-n-slate-12">
              {{ selectedContact.name }}
            </span>
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-x"
              @click="clearSelectedContact"
            />
          </div>
          <!-- Search + inline add-contact -->
          <div v-else class="flex items-center gap-1.5">
            <div class="relative flex-1 min-w-0">
              <input
                v-model="contactSearchQuery"
                type="text"
                :placeholder="$t('TICKETS.FORM.CONTACT_PLACEHOLDER')"
                class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
                @input="handleContactInput"
                @focus="showContactDropdown = searchResults.length > 0"
              />
              <div
                v-if="
                  showContactDropdown &&
                  (searchResults.length > 0 || isSearching)
                "
                class="absolute z-50 w-full mt-1 overflow-y-auto border rounded-md shadow-lg bg-n-solid-2 border-n-weak max-h-40"
              >
                <div
                  v-if="isSearching"
                  class="flex items-center justify-center p-2"
                >
                  <span class="text-xs text-n-slate-11">
                    {{ $t('TICKETS.FORM.SEARCHING') }}
                  </span>
                </div>
                <div
                  v-for="result in searchResults"
                  :key="result.id"
                  class="flex items-center gap-2 p-2 cursor-pointer hover:bg-n-alpha-2"
                  @click="selectContact(result)"
                >
                  <Avatar
                    :name="result.name"
                    :src="result.thumbnail"
                    :size="22"
                  />
                  <div class="flex flex-col min-w-0">
                    <span class="text-sm truncate text-n-slate-12">
                      {{ result.name }}
                    </span>
                    <span
                      v-if="result.email"
                      class="text-xs truncate text-n-slate-10"
                    >
                      {{ result.email }}
                    </span>
                  </div>
                </div>
                <div
                  v-if="
                    !isSearching &&
                    searchResults.length === 0 &&
                    contactSearchQuery.length >= 2
                  "
                  class="p-2 text-xs text-n-slate-11"
                >
                  {{ $t('TICKETS.FORM.NOT_FOUND') }}
                </div>
              </div>
            </div>
            <Button
              v-if="showAddContactButton"
              v-tooltip.top="$t('TICKETS.FORM.ADD_NEW_CONTACT')"
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-plus"
              class="shrink-0"
              @click="openCreateNewContactDialog"
            />
          </div>
        </div>
      </div>

      <!-- Tipo de ticket (category / subcategory) -->
      <CategorySelector
        v-model:category-id="ticketCategoryId"
        v-model:subcategory-id="ticketSubcategoryId"
        @change="handleCategoryChange"
      />

      <!-- Metadata grid -->
      <section class="grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2">
        <!-- Priority -->
        <div class="flex items-start gap-3 min-w-0">
          <span
            class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
          >
            <span
              class="i-lucide-flag"
              :class="PRIORITY_FLAG[ticket.priority]"
            />
            {{ $t('TICKETS.FORM.PRIORITY') }}
          </span>
          <div class="flex flex-1 min-w-0">
            <SelectMenu
              :model-value="ticket.priority"
              :options="priorityOptions"
              :label="priorityLabel"
              sub-menu-position="bottom"
              @update:model-value="ticket.priority = $event"
            />
          </div>
        </div>

        <!-- Channel -->
        <div class="flex items-start gap-3 min-w-0">
          <span
            class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
          >
            <span class="i-lucide-radio text-n-slate-10" />
            {{ $t('TICKETS.FORM.CHANNEL') }}
            <span class="text-n-ruby-9">*</span>
          </span>
          <div class="flex flex-1 min-w-0">
            <span
              v-if="conversationChannel"
              class="mt-1 text-sm text-n-slate-12"
            >
              {{ channelLabel }}
            </span>
            <SelectMenu
              v-else
              :model-value="ticket.channel"
              :options="channelOptions"
              :label="channelLabel"
              sub-menu-position="bottom"
              @update:model-value="ticket.channel = $event"
            />
          </div>
        </div>

        <!-- Assignee -->
        <div class="flex items-start gap-3 min-w-0">
          <span
            class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
          >
            <span class="i-lucide-user text-n-slate-10" />
            {{ $t('TICKETS.FORM.ASSIGNEE') }}
          </span>
          <div class="flex flex-col flex-1 min-w-0 gap-1.5">
            <SelectMenu
              v-if="!assignToMe"
              :model-value="ticket.assignee_id"
              :options="assigneeOptions"
              :label="assigneeLabel"
              sub-menu-position="bottom"
              @update:model-value="ticket.assignee_id = $event"
            />
            <label
              v-if="currentUser"
              class="flex items-center gap-2 text-xs cursor-pointer select-none text-n-slate-11"
            >
              <input v-model="assignToMe" type="checkbox" class="rounded" />
              {{ $t('TICKETS.FORM.ASSIGN_TO_ME_CHECKBOX') }}
            </label>
          </div>
        </div>

        <!-- Team -->
        <div class="flex items-start gap-3 min-w-0">
          <span
            class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
          >
            <span class="i-lucide-users text-n-slate-10" />
            {{ $t('TICKETS.SIDEBAR.TEAM') }}
          </span>
          <div class="flex flex-1 min-w-0">
            <SelectMenu
              :model-value="ticket.team_id"
              :options="teamOptions"
              :label="teamLabel"
              sub-menu-position="bottom"
              @update:model-value="ticket.team_id = $event"
            />
          </div>
        </div>

        <!-- Contract (CEA — only when enabled) -->
        <div
          v-if="isContractsEnabled"
          class="flex items-start gap-3 min-w-0 sm:col-span-2"
        >
          <span
            class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
          >
            <span class="i-lucide-file-text text-n-slate-10" />
            {{ $t('TICKETS.FORM.CONTRACT_NUMBER') }}
          </span>
          <div class="flex flex-col flex-1 min-w-0 gap-1">
            <input
              v-model="ticket.contract_number"
              type="text"
              :placeholder="$t('TICKETS.FORM.CONTRACT_NUMBER_PLACEHOLDER')"
              class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1"
              :class="[
                contractNumberError
                  ? 'border-n-ruby-8 focus:outline-n-ruby-8'
                  : 'border-n-weak focus:outline-n-brand',
              ]"
            />
            <p v-if="contractNumberError" class="text-xs text-n-ruby-9">
              {{ contractNumberError }}
            </p>
          </div>
        </div>
      </section>

      <!-- Dynamic workflow fields: one collapsible per group (collapsed by default) -->
      <div
        v-for="grp in workflowFieldGroups"
        :key="grp.key"
        class="flex flex-col border shrink-0 rounded-xl border-n-weak bg-n-solid-2"
      >
        <button
          type="button"
          class="flex items-center justify-between w-full gap-2 px-3 py-2.5 text-left hover:bg-n-alpha-1"
          :aria-expanded="isWorkflowGroupOpen(grp.key)"
          :aria-controls="`create-ticket-wf-${grp.key}`"
          @click="toggleWorkflowGroup(grp.key)"
        >
          <div class="flex items-center flex-1 min-w-0 gap-2">
            <span
              v-if="grp.key === 'address'"
              class="shrink-0 i-lucide-map-pin size-4 text-n-slate-11"
              aria-hidden="true"
            />
            <span class="text-xs font-semibold truncate text-n-slate-12">
              {{ $t(grp.titleKey) }}
            </span>
          </div>
          <span
            class="shrink-0 i-lucide-chevron-down size-5 text-n-slate-11 transition-transform duration-200"
            :class="{ 'rotate-180': isWorkflowGroupOpen(grp.key) }"
            aria-hidden="true"
          />
        </button>
        <div
          v-show="isWorkflowGroupOpen(grp.key)"
          :id="`create-ticket-wf-${grp.key}`"
          class="flex flex-col gap-2 p-3 border-t border-n-weak"
          role="region"
        >
          <div
            v-for="wf in grp.fields"
            :key="wf.key"
            class="flex flex-col gap-1"
          >
            <template v-if="wf.type === 'address'">
              <div class="grid grid-cols-2 gap-2">
                <div
                  v-for="sf in getAddressSubFields(wf)"
                  :key="sf"
                  :class="
                    ADDRESS_FIELD_MAP[sf]?.colSpan === 2 ? 'col-span-2' : ''
                  "
                >
                  <input
                    v-if="ADDRESS_FIELD_MAP[sf]"
                    :value="
                      customAttributeValues[addressCustomAttributeKey(wf, sf)] ||
                      ''
                    "
                    type="text"
                    :placeholder="$t(ADDRESS_FIELD_MAP[sf].placeholder)"
                    class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
                    @input="
                      customAttributeValues[addressCustomAttributeKey(wf, sf)] =
                        $event.target.value
                    "
                  />
                </div>
              </div>
            </template>
            <template v-else>
              <label class="text-xs font-medium text-n-slate-11">
                {{ wf.definition?.attributeDisplayName || wf.label || wf.key }}
                <span v-if="wf.priority === 'required'" class="text-n-ruby-9">
                  *
                </span>
              </label>
              <select
                v-if="wf.definition?.attributeDisplayType === 'list'"
                :value="customAttributeValues[wf.key] || ''"
                class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
                @change="customAttributeValues[wf.key] = $event.target.value"
              >
                <option value="">--</option>
                <option
                  v-for="opt in wf.definition.attributeValues || []"
                  :key="opt"
                  :value="opt"
                >
                  {{ opt }}
                </option>
              </select>
              <input
                v-else-if="wf.definition?.attributeDisplayType === 'checkbox'"
                type="checkbox"
                :checked="customAttributeValues[wf.key]"
                class="rounded"
                @change="customAttributeValues[wf.key] = $event.target.checked"
              />
              <input
                v-else-if="wf.definition?.attributeDisplayType === 'date'"
                type="date"
                :value="customAttributeValues[wf.key] || ''"
                class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
                @change="customAttributeValues[wf.key] = $event.target.value"
              />
              <input
                v-else-if="wf.definition?.attributeDisplayType === 'number'"
                type="number"
                :value="customAttributeValues[wf.key] || ''"
                class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
                @change="customAttributeValues[wf.key] = $event.target.value"
              />
              <input
                v-else
                type="text"
                :value="customAttributeValues[wf.key] || ''"
                class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
                @input="customAttributeValues[wf.key] = $event.target.value"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- Description -->
      <section class="flex flex-col gap-2">
        <h3
          class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
        >
          <span class="i-lucide-align-left" />
          {{ $t('TICKETS.FORM.DESCRIPTION') }}
        </h3>
        <div class="p-2 border rounded-xl border-n-weak bg-n-solid-2">
          <WootMessageEditor
            v-model="ticket.description"
            editor-id="create-ticket-description"
            :placeholder="$t('TICKETS.FORM.DESCRIPTION_PLACEHOLDER')"
            is-private
            :enable-canned-responses="false"
            :enable-variables="false"
            class="min-h-[80px] [&_.ProseMirror]:max-h-[260px] [&_.ProseMirror]:overflow-y-auto"
          />
        </div>
      </section>

      <!-- Attachments -->
      <section class="flex flex-col gap-2">
        <h3
          class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
        >
          <span class="i-lucide-paperclip" />
          {{ $t('TICKETS.ATTACHMENTS.TITLE') }}
          <span v-if="pendingFiles.length" class="text-n-slate-9">
            {{ pendingFiles.length }}
          </span>
        </h3>

        <ul v-if="pendingFiles.length" class="flex flex-col gap-2">
          <li
            v-for="(file, index) in pendingFiles"
            :key="index"
            class="flex items-center gap-2.5 px-3 py-2 border rounded-lg group border-n-weak bg-n-solid-2"
          >
            <span class="shrink-0 i-lucide-paperclip text-n-slate-10" />
            <span class="flex-1 text-sm truncate text-n-slate-12">
              {{ file.name }}
            </span>
            <span class="text-xs shrink-0 text-n-slate-10">
              {{ formatFileSize(file.size) }}
            </span>
            <button
              type="button"
              class="flex items-center justify-center w-6 h-6 rounded-md opacity-0 group-hover:opacity-100 text-n-slate-10 hover:text-n-ruby-9 hover:bg-n-alpha-1"
              :aria-label="$t('TICKETS.ATTACHMENTS.REMOVE_FILE')"
              @click.stop="removeFile(index)"
            >
              <span class="i-lucide-x" />
            </button>
          </li>
        </ul>

        <button
          type="button"
          class="flex flex-col items-center justify-center gap-1 px-4 py-6 text-sm text-center transition-colors border-2 border-dashed rounded-xl"
          :class="
            isDragging
              ? 'border-n-brand bg-n-alpha-1 text-n-slate-12'
              : 'border-n-weak text-n-slate-10 hover:border-n-slate-6'
          "
          @dragover="onDragOver"
          @dragleave="onDragLeave"
          @drop="onDrop"
          @click="fileInputRef?.click()"
        >
          <span class="text-lg i-lucide-cloud-upload" />
          <span>
            {{
              isDragging
                ? $t('TICKETS.ATTACHMENTS.DROP_HERE')
                : $t('TICKETS.ATTACHMENTS.DRAG_AND_DROP_HINT')
            }}
          </span>
          <input
            ref="fileInputRef"
            type="file"
            multiple
            class="sr-only"
            @change="onFileInput"
          />
        </button>
      </section>
    </div>
  </Dialog>

  <CreateNewContactDialog
    ref="createNewContactDialogRef"
    @create="onContactCreated"
  />
</template>
